import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CustomerSummaryMapping } from "../mappings/customer-summary.mapping";
import { EnvironmentService } from "./environment.service";
import { CustomerMapping } from "../mappings/customer-result.mapping";
import { CustomerCreateMapping } from "../mappings/customer-create.mapping";
import { ICustomerSummaryModel } from "../models/customer/customer-summary.model";
import { ICustomerSummaryDtoModel } from "../models/customer/dto/customer-summary-dto.model";
import { ICustomerCreateResponseModel } from "../models/customer/customer-create.response.model";
import { ICustomerCreateDtoModel } from "../models/customer/dto/customer-create-dto.model";
import { ICustomerCreateResponseDtoModel } from "../models/customer/dto/customer-create-dto.response.model";
import { ISearchCustomers } from "../models/customer/customer-search.model";
import { IPaginationSearchRequestBaseModel } from "../models/generic/pagination/pagination-search-base.model";
import { IPaginationResponseModel } from "../models/generic/pagination/pagination-response.base.model";
import { ICustomerResultModel } from "../models/customer/customer-result.model";
import { ICustomerResultDtoModel } from "../models/customer/dto/customer-result-dto.model";
import { IJcontrolCustomerCreationModel } from "../models/legacy-info/jcontrol-customer-creation.model";
import { IJcontrolCustomerCreationDtoModel } from "../models/legacy-info/jcontrol-customer-creation-dto.model";
import { ApplicationContextService } from "./application-context.service";
import { IGarageVehicleModel } from "../models/garage/garage-vehicle.model";
import { IGarageVehicleDtoModel } from "../models/garage/dto/garage-vehicle-dto.model";
import { GarageVehicleMapper } from "../mappings/garage-vehicle.mapping";

@Injectable({
  providedIn: 'root'
})
export class CustomerDetailsService {
  private customerMapping: CustomerMapping;
  private customerCreateMapping: CustomerCreateMapping;
  private customerSummaryMapping: CustomerSummaryMapping;
  private garageVehicleMapper: GarageVehicleMapper;

  constructor(private readonly httpClient: HttpClient, public envService: EnvironmentService, private applicationContext: ApplicationContextService) {
    this.customerSummaryMapping = new CustomerSummaryMapping();
    this.customerMapping = new CustomerMapping();
    this.customerCreateMapping = new CustomerCreateMapping();
    this.garageVehicleMapper = new GarageVehicleMapper();
  }

  private path() {
    if (this.applicationContext.isPublic()) {
      return `${this.envService.settings.publicApiUrl}/Customers/summary-fallback`;
    }

    return `${this.envService.settings.apiUrl}/Customers`;
  }

  private garagePath() {
    if (this.applicationContext.isPublic()) {
      return `${this.envService.settings.publicApiUrl}/Customers/vehicles`;
    }
    return `${this.envService.settings.apiUrl}/Garages`;
  }

  public get(id: string): Observable<ICustomerSummaryModel> {
    if (this.applicationContext.isPublic()) {
      return this.httpClient.get<ICustomerSummaryDtoModel>(`${this.path()}?customerId=${id}&branchCode=${this.applicationContext.getBranchCodeFromUrl()}`).pipe(
        map(dto => {

          //if the fallback customer id is different to the one we requested
          //it's because the customer has multiple WEB accounts
          //we need to store the fallback id for use in subsequent requests
          if (dto.id != id) {
            window.sessionStorage.setItem(id + '-fallback', dto.id);
          }

          return this.customerSummaryMapping.toApplicationModel(dto)
        })
      );
    }

    return this.httpClient.get<ICustomerSummaryDtoModel>(`${this.path()}/${id}/summary`).pipe(
      map(dto => this.customerSummaryMapping.toApplicationModel(dto))
    );
  }

  public save(model: ICustomerCreateDtoModel): Observable<ICustomerCreateResponseModel> {
    return this.httpClient.post<ICustomerCreateResponseDtoModel>(this.path(), model).pipe(
      map(resultDto => this.customerCreateMapping.toResponseApplicationModel(resultDto))
    )
  }

  public search(paginationSearchModel: IPaginationSearchRequestBaseModel<ISearchCustomers>): Observable<IPaginationResponseModel<ICustomerResultModel>> {
    let params = new HttpParams();

    localStorage.setItem('previousCustomerSearch', JSON.stringify({
      'mode': paginationSearchModel?.searchModel?.mode,
      'customerAccountNumber': paginationSearchModel?.searchModel?.customerAccountNumber,
      'customerAccountNumberPrefix': paginationSearchModel?.searchModel?.customerAccountNumberPrefix,
      'customerNameContains': paginationSearchModel?.searchModel?.customerNameContains,
      'surnameBegins': paginationSearchModel?.searchModel?.surnameBegins,
      'companyNameBegins': paginationSearchModel?.searchModel?.companyNameBegins,
      'postalCodeBegins': paginationSearchModel?.searchModel?.postalCodeBegins,
      'telephoneNumberBegins': paginationSearchModel?.searchModel?.telephoneNumberBegins,
      'emailAddressBegins': paginationSearchModel?.searchModel?.emailAddressBegins,
      'legacyAccountNumber': paginationSearchModel?.searchModel?.legacyAccountNumber,
      'includeActiveAccountsOnly': paginationSearchModel?.searchModel?.includeActiveAccountsOnly
    }));

    if (paginationSearchModel != null) {
      if (paginationSearchModel?.searchModel?.customerNameContains) {
        params = params.append("name", paginationSearchModel?.searchModel?.customerNameContains);
      }
      if (paginationSearchModel?.searchModel?.customerAccountNumber) {
        params = params.append("accountNumber", paginationSearchModel?.searchModel?.customerAccountNumber);
      }
      if (paginationSearchModel?.searchModel?.customerAccountNumberPrefix) {
        params = params.append("accountNumberPrefix", paginationSearchModel?.searchModel?.customerAccountNumberPrefix);
      }
      if (paginationSearchModel?.searchModel?.surnameBegins) {
        params = params.append("lastName", paginationSearchModel?.searchModel?.surnameBegins);
      }
      if (paginationSearchModel?.searchModel?.companyNameBegins) {
        params = params.append("companyName", paginationSearchModel?.searchModel?.companyNameBegins);
      }
      if (paginationSearchModel?.searchModel?.postalCodeBegins) {
        params = params.append("postalCode", paginationSearchModel?.searchModel?.postalCodeBegins);
      }
      if (paginationSearchModel?.searchModel?.telephoneNumberBegins) {
        params = params.append("telephoneNumber", paginationSearchModel?.searchModel?.telephoneNumberBegins);
      }
      if (paginationSearchModel?.searchModel?.emailAddressBegins) {
        params = params.append("emailAddress", paginationSearchModel?.searchModel?.emailAddressBegins);
      }
      if (paginationSearchModel?.searchModel?.legacyAccountNumber) {
        params = params.append("legacyAccountNumber", paginationSearchModel?.searchModel?.legacyAccountNumber);
      }
      if (paginationSearchModel?.searchModel?.includeActiveAccountsOnly) {
        params = params.append("includeActiveAccountsOnly", paginationSearchModel?.searchModel?.includeActiveAccountsOnly);
      }

      params = params.append("currentPage", paginationSearchModel.currentpage);
      params = params.append("rowsPerPage", paginationSearchModel.rowsperpage);
      if (this.applicationContext.isApplicationEmbedded()) {
        params = params.append("sortByBranchCode", this.applicationContext.getBranchCodeFromUrl());
      }
    }
    return this.httpClient.get<any>(this.path(), { params }).pipe(
      map(dtos => {
        return {
          total: dtos.total,
          data: dtos.customers ? (dtos.customers as ICustomerResultDtoModel[]).map(dto => this.customerMapping.toApplicationModel(dto)) : []
        } as IPaginationResponseModel<ICustomerResultModel>
      })
    );
  }

  public getJcontrolCustomerCreationData(id: string): Observable<IJcontrolCustomerCreationModel> {

    let branch = this.applicationContext.getBranchCodeFromUrl();

    if (branch == 'ALL') {
      branch = 'SNG';
    }

    return this.httpClient.get<IJcontrolCustomerCreationDtoModel>(`${this.path()}/${id}/jcontrolCustomerCreationData?branchCode=${branch}`).pipe(
      map(dto => this.customerCreateMapping.toJcontrolCustomerCreateModel(dto))
    );
  }

  public updateCustomerLegacyInfo(customerId: string, legacyCustomerId: number, accountNumber: string, siteCode: string): Observable<any> {

    let req: any = {
      customerId: customerId
    }

    req['legacyCustomerId_' + siteCode] = legacyCustomerId;
    req['legacyAccountNo_' + siteCode] = accountNumber;

    return this.httpClient.put<any>(`${this.path()}/${customerId}/legacy`, req)

  }

  public emailAddressLookup(emailAddress: string): Observable<any> {
    return this.httpClient.get<any>(`${this.path()}/emailAddressLookup?emailAddress=${emailAddress}`).pipe(
      map(dtos => {
        return {
          total: dtos.length,
          data: dtos
        }
      })
    );
  }

  public getCustomerVehicles(customerId: string): Observable<IPaginationResponseModel<IGarageVehicleModel>> {
    return this.httpClient.get<IGarageVehicleDtoModel[]>(`${this.garagePath()}?customerId=${customerId}`).pipe(
      map(dtos => this.garageVehicleMapper.toApplicationModels(dtos)),
      map(models => {
        return {
          total: models.length,
          data: models
        } as IPaginationResponseModel<IGarageVehicleModel>
      })
    );
  }
}
